import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import RegionalService from '../services/RegionalService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import { useAuth } from '../contexts/AuthContext';
import materiaService from '../services/MateriaService';
import areaService from '../services/AreaService';
import usuarioService from '../services/UsuarioService';
import { isGptClassicEnabled as hasGptClassicFeature } from '../utils/adminMenu';
import { MATERIA_TIPO_GRADO, MATERIA_TIPO_OPTIONS, normalizeMateriaTipo } from '../utils/materiaTypes';
import SearchableSelect from './common/SearchableSelect';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { navigateAdminMenuItem } from './sidebar/menuConfig';

const AdminEditMateria = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { materiaId } = useParams();
  const { user } = useAuth();
  const materiaState = location.state?.materia || {};
  const stateRegionalId = location.state?.idRegional || null;

  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => stateRegionalId || RegionalService.getActiveRegionalId(), [stateRegionalId]);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional';
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(RegionalService.getFeatureFlags()), []);
  const handleAdminMenuSelect = (item) => navigateAdminMenuItem(navigate, item.id);
  const [areas, setAreas] = useState([]);
  const [anios, setAnios] = useState([]);
  const [profesoresDisponibles, setProfesoresDisponibles] = useState([]);
  const [profesoresLoading, setProfesoresLoading] = useState(false);
  const [profesoresError, setProfesoresError] = useState('');
  const [selectedProfesorId, setSelectedProfesorId] = useState('');
  const [profesoresAsignados, setProfesoresAsignados] = useState([]);
  const [isAssociating, setIsAssociating] = useState(false);

  const [form, setForm] = useState({
    nombre: materiaState.nombre || materiaState.Nombre || '',
    codigo: materiaState.codigo || materiaState.Codigo || '',
    descripcion: materiaState.descripcion || materiaState.Descripcion || '',
    tipoMateria: normalizeMateriaTipo(materiaState.tipoMateria || materiaState.TipoMateria),
    idArea: materiaState.idArea || materiaState.IdArea || '',
    idAnio: materiaState.idAnio || materiaState.IdAnio || '',
    idRegional: activeRegionalId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const getProfesorId = (profesor) => String(profesor?.id || profesor?.Id || '');
  const getProfesorNombre = (profesor) => (
    profesor?.displayName ||
    profesor?.DisplayName ||
    `${profesor?.nombre || profesor?.Nombre || ''} ${profesor?.apellido || profesor?.Apellido || ''}`.trim() ||
    profesor?.email ||
    profesor?.Email ||
    'Profesor'
  );
  const getProfesorEmail = (profesor) => profesor?.email || profesor?.Email || '';

  const profesoresDisponiblesParaAgregar = useMemo(() => {
    const asignadosIds = new Set(
      profesoresAsignados
        .map((profesor) => getProfesorId(profesor))
        .filter(Boolean)
    );

    return profesoresDisponibles
      .filter((profesor) => {
        const profesorId = getProfesorId(profesor);
        return profesorId && !asignadosIds.has(profesorId);
      })
      .sort((a, b) => getProfesorNombre(a).localeCompare(getProfesorNombre(b), 'es', { sensitivity: 'base' }));
  }, [profesoresAsignados, profesoresDisponibles]);

  const profesorSearchOptions = useMemo(() => (
    profesoresDisponiblesParaAgregar.map((profesor) => {
      const nombre = getProfesorNombre(profesor);
      const email = getProfesorEmail(profesor);

      return {
        value: getProfesorId(profesor),
        label: nombre,
        meta: email || 'Sin email',
        searchText: `${nombre} ${email}`
      };
    })
  ), [profesoresDisponiblesParaAgregar]);

  useEffect(() => {
    if (!materiaId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [materiaId, navigate]);

  useEffect(() => {
    const fetchMateria = async () => {
      if (materiaState && Object.keys(materiaState).length > 0) return;
      if (!materiaId) return;
      try {
        const m = await materiaService.getMateriaById(materiaId, { idRegional: activeRegionalId });
        if (m) {
          setForm({
            nombre: m.nombre || m.Nombre || '',
            codigo: m.codigo || m.Codigo || '',
            descripcion: m.descripcion || m.Descripcion || '',
            tipoMateria: normalizeMateriaTipo(m.tipoMateria || m.TipoMateria),
            idArea: m.idArea || m.IdArea || '',
            idAnio: m.idAnio || m.IdAnio || '',
            idRegional: m.idRegional || m.IdRegional || activeRegionalId || ''
          });
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar la materia.');
        setMessageType('error');
      }
    };
    fetchMateria();
  }, [materiaId, materiaState, activeRegionalId]);

  useEffect(() => {
    const fetchProfesores = async () => {
      if (!activeRegionalId) return;
      setProfesoresLoading(true);
      setProfesoresError('');
      try {
        const list = await usuarioService.getProfesoresPorRegional(activeRegionalId);
        setProfesoresDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setProfesoresError(error.message || 'No pudimos cargar los profesores.');
      } finally {
        setProfesoresLoading(false);
      }
    };

    fetchProfesores();
  }, [activeRegionalId]);

  useEffect(() => {
    const fetchProfesoresAsignados = async () => {
      if (!materiaId) return;
      try {
        const list = await materiaService.getProfesoresPorMateria(materiaId, { idRegional: activeRegionalId });
        setProfesoresAsignados(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los profesores asociados.');
        setMessageType('error');
      }
    };

    fetchProfesoresAsignados();
  }, [materiaId]);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const list = await areaService.getAreasActivas(activeRegionalId);
        setAreas(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar las áreas.');
        setMessageType('error');
      }
    };
    fetchAreas();

    const fetchAnios = async () => {
      try {
        const list = await materiaService.getAniosActivos();
        setAnios(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('No pudimos cargar años:', error);
      }
    };
    fetchAnios();

    if (activeRegionalId && !form.idRegional) {
      setForm((prev) => ({ ...prev, idRegional: activeRegionalId }));
    }
  }, [activeRegionalId, form.idRegional]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddProfesor = () => {
    if (!selectedProfesorId) return;
    const profesor = profesoresDisponibles.find((p) => getProfesorId(p) === selectedProfesorId);
    if (!profesor) return;
    if (profesoresAsignados.some((p) => getProfesorId(p) === selectedProfesorId)) return;
    setProfesoresAsignados((prev) => [...prev, profesor]);
    setSelectedProfesorId('');
  };

  const handleRemoveProfesor = async (profesorId) => {
    if (!materiaId || !profesorId) return;
    try {
      await materiaService.removeProfesores(materiaId, [profesorId], { idRegional: activeRegionalId });
      setProfesoresAsignados((prev) => prev.filter((p) => getProfesorId(p) !== profesorId));
      setMessage('Profesor desasociado correctamente.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos desasociar el profesor.');
      setMessageType('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre de la materia.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área para asociar la materia.');
      setMessageType('error');
      return;
    }
    if (!form.tipoMateria) {
      setMessage('Selecciona el tipo de materia.');
      setMessageType('error');
      return;
    }
    if (!form.idRegional) {
      setMessage('No se pudo determinar el regional activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      await materiaService.updateMateria(materiaId, {
        nombre: form.nombre,
        codigo: form.codigo,
        descripcion: form.descripcion,
        tipoMateria: normalizeMateriaTipo(form.tipoMateria),
        idRegional: form.idRegional,
        idArea: form.idArea,
        idAnio: form.idAnio || null
      });
      setMessage('Materia actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'materias' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar la materia.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignProfesores = async () => {
    if (!materiaId) return;
    const ids = profesoresAsignados.map((p) => p.id || p.Id).filter(Boolean);
    if (ids.length === 0) {
      setMessage('Agrega al menos un profesor para asociar.');
      setMessageType('error');
      return;
    }

    setIsAssociating(true);
    setMessage('Asociando profesores a la materia...');
    setMessageType('info');
    try {
      const res = await materiaService.assignProfesores(materiaId, ids, { idRegional: activeRegionalId });
      const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
      const reactivados = res?.reactivados || res?.Reactivados || [];
      const yaAsociados = res?.yaAsociados || res?.YaAsociados || [];
      const noEncontrados = res?.profesoresNoEncontrados || res?.ProfesoresNoEncontrados || [];
      const noRol = res?.profesoresNoRol || res?.ProfesoresNoRol || [];

      const summaryParts = [];
      if (nuevos.length) summaryParts.push(`Asociados nuevos: ${nuevos.length}`);
      if (reactivados.length) summaryParts.push(`Reactivados: ${reactivados.length}`);
      if (yaAsociados.length) summaryParts.push(`Ya asociados: ${yaAsociados.length}`);
      if (noEncontrados.length) summaryParts.push(`No encontrados: ${noEncontrados.length}`);
      if (noRol.length) summaryParts.push(`Sin rol Profesor: ${noRol.length}`);

      setMessage(`Asociación completada. ${summaryParts.join(' | ') || ''}`.trim());
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos asociar los profesores.');
      setMessageType('error');
    } finally {
      setIsAssociating(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            activeItemId="materias"
            onSelect={handleAdminMenuSelect}
          />
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar materia</h1>
            <p className="page-subtitle">Ajusta los datos de la materia en el regional {activeRegionalName}.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <UserAccountMenu />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-book" />
                <span>Datos de la materia</span>
              </div>
            </div>
            <div className="card-content" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '1.5rem' }}>
              <div>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Completa o ajusta los datos de la materia.
                </p>
                <form onSubmit={handleSubmit} className="prof-create-form">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Materia X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Código</label>
                    <input
                      type="text"
                      value={form.codigo || ''}
                      onChange={(e) => handleChange('codigo', e.target.value)}
                      placeholder="Ej: AED-101"
                    />
                  </div>
                  <div className="form-field">
                    <label>Descripción</label>
                    <textarea
                      value={form.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      placeholder="Descripción de la materia"
                      style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Tipo de materia</label>
                    <select
                      value={form.tipoMateria || MATERIA_TIPO_GRADO}
                      onChange={(e) => handleChange('tipoMateria', e.target.value)}
                    >
                      {MATERIA_TIPO_OPTIONS.map((tipo) => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Área (obligatorio)</label>
                    <select
                      value={form.idArea}
                      onChange={(e) => handleChange('idArea', e.target.value)}
                    >
                      <option value="">Selecciona un área</option>
                      {areas.map((area) => (
                        <option key={area.id || area.Id} value={area.id || area.Id}>
                          {area.nombre || area.name || area.descripcion || area.detalle || 'Área'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Año</label>
                    <select
                      value={form.idAnio || ''}
                      onChange={(e) => handleChange('idAnio', e.target.value)}
                    >
                      <option value="">Sin año</option>
                      {anios.map((anio) => (
                        <option key={anio.id || anio.Id} value={anio.id || anio.Id}>
                          {anio.valor || anio.Valor}
                        </option>
                      ))}
                    </select>
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {(isSubmitting || isAssociating) && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'materias' } })}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Guardar cambios</span>
                    </button>
                  </div>
                </form>
              </div>

              <div style={{ borderLeft: '1px solid var(--gray-200)', paddingLeft: '1.25rem' }}>
                <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                  <i className="fas fa-chalkboard-teacher" style={{ color: '#3b82f6' }} />
                  <span>Profesores</span>
                </div>
                <p style={{ marginTop: 0, color: '#475569', fontSize: '0.95rem' }}>
                  Selecciona los profesores para esta materia. Los ya asociados se muestran abajo.
                </p>

                {profesoresLoading && <p>Cargando profesores...</p>}
                {profesoresError && <p style={{ color: '#b91c1c' }}>{profesoresError}</p>}

                {!profesoresLoading && profesoresDisponibles.length > 0 && (
                  <div className="form-field">
                    <label>Buscar profesor</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <SearchableSelect
                        value={selectedProfesorId}
                        onChange={setSelectedProfesorId}
                        options={profesorSearchOptions}
                        placeholder="Escribe nombre o email del profesor"
                        emptyMessage="No encontramos profesores con esa búsqueda."
                        noOptionsMessage="No hay profesores disponibles para agregar."
                      />
                      <button type="button" className="btn btn-primary" onClick={handleAddProfesor} disabled={!selectedProfesorId}>
                        <i className="fas fa-plus" />
                      </button>
                    </div>
                    {profesoresDisponiblesParaAgregar.length === 0 && (
                      <p style={{ color: '#6b7280', margin: '0.35rem 0 0 0' }}>
                        Todos los profesores disponibles ya están agregados a la lista.
                      </p>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Profesores asignados</p>
                  {profesoresAsignados.length === 0 && (
                    <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay profesores asignados.</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {profesoresAsignados.map((prof) => {
                      const name = getProfesorNombre(prof);
                      const profId = getProfesorId(prof);
                      return (
                        <div key={profId} style={{ border: '1px solid var(--gray-200)', borderRadius: '999px', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#f8fafc' }}>
                          <i className="fas fa-chalkboard-teacher" style={{ color: '#3b82f6' }} />
                          <span>{name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveProfesor(profId)}
                            style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                  <button type="button" className="btn btn-primary" onClick={handleAssignProfesores} disabled={isAssociating}>
                    {isAssociating ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-link" />}
                    <span>{isAssociating ? 'Asociando...' : 'Asociar profesor'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditMateria;
